import { useCallback, useEffect, useRef } from 'react';

import { SessionStorageManager, useApplicationState } from '@atlas/core';

import { POSTHOG_CTA, POSTHOG_EVENTS, POSTHOG_SESSION_KEYS } from 'common/constants';

import usePostHog from './usePostHog';

const useDepositPostHog = () => {
  const postHog = usePostHog();
  const isDepositStartedRef = useRef(false);
  const isInitializedRef = useRef(false);
  const pageReferRef = useRef(
    SessionStorageManager.getItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER),
  );
  SessionStorageManager.removeItem(POSTHOG_SESSION_KEYS.DEPOSIT_PAGE_REFER);
  const {
    account,
    wallet: { detailedBalance: { BONUS = 0, CASH = 0 } = {} },
  } = useApplicationState();

  const getBaseData = useCallback(() => {
    return {
      client_id: account.id,
      page_refer: pageReferRef.current,
      user_bonus_balance: BONUS,
      user_real_balance: CASH,
    };
  }, [account.id, BONUS, CASH]);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initData = {
      ...getBaseData(),
      amount: null,
      bonus_id: null,
      cta_clicked: null,
      manual_closed_timestamp: null,
      payment_method: null,
      shown_timestamp: Date.now(),
    };

    postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, initData);
  }, [getBaseData, postHog]);

  useEffect(() => {
    return () => {
      if (!isDepositStartedRef.current) {
        postHog.send(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, { manual_closed_timestamp: Date.now() });
      }
    };
  }, []);

  const handleDepositStart = useCallback(
    ({ amount, bonus_id, payment_method }) => {
      const startData = {
        amount,
        bonus_id,
        cta_clicked: POSTHOG_CTA.DEPOSIT,
        payment_method,
      };

      isDepositStartedRef.current = true;
      postHog.concat(POSTHOG_EVENTS.DEP_POP_UP_SHOWN, { ...getBaseData(), ...startData });
    },
    [getBaseData, postHog],
  );

  const handleDepositSuccess = useCallback(
    ({ transaction_id }) => {
      postHog.send(
        POSTHOG_EVENTS.DEP_POP_UP_SHOWN,
        transaction_id != null ? { transaction_id } : undefined,
      );
    },
    [postHog],
  );

  return { handleDepositStart, handleDepositSuccess };
};

export default useDepositPostHog;
